import React, { useCallback, useEffect, useState } from 'react';
import SuperadminLayout from '../../components/SuperadminLayout';
import {
  listAdminBanks, listBankTransfers, listCompanies, listGroups, purgeAdminBank, restoreAdminBank,
  resetAdminBankUsername, setAdminBankSeq, setAdminBankUsername, transferAdminBank,
} from '../../api/superadmin';
import './SuperadminBanksPage.css';

// Banques / POS de TOUTES les companies (2026-09-26, refaite pour etre claire) :
//  - Transferer un POS vers une autre company ;
//  - Restaurer une banque "supprimee" (archivee) par une company.
// Chaque action s'ouvre dans une FENETRE au centre de l'ecran (avant : le
// formulaire s'ouvrait en haut de la page, hors de vue -> "les boutons ne
// marchent pas").

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const apiError = (err, fallback) => {
  try {
    const d = JSON.parse(err.message);
    return d.detail || Object.values(d)[0] || fallback;
  } catch (_) {
    return err?.message?.includes('404') ? 'Fonction introuvable sur le serveur (backend pas encore mis a jour ?).' : fallback;
  }
};

const Modal = ({ title, onClose, children }) => (
  <div className="sab-overlay" onClick={onClose}>
    <div className="sab-modal" onClick={(e) => e.stopPropagation()}>
      <div className="sab-modal-header">
        <span>{title}</span>
        <button type="button" className="sab-close" onClick={onClose} aria-label="Fermer">✕</button>
      </div>
      <div className="sab-modal-body">{children}</div>
    </div>
  </div>
);

const SuperadminBanksPage = () => {
  const [query, setQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [companies, setCompanies] = useState([]);
  const [transfers, setTransfers] = useState([]);

  // Fenetre ouverte : { type: 'transfer' | 'restore' | 'result', bank, ... }
  const [modal, setModal] = useState(null);
  const [targetCompany, setTargetCompany] = useState('');
  const [targetGroups, setTargetGroups] = useState([]);
  const [targetGroup, setTargetGroup] = useState('');
  const [note, setNote] = useState('');
  const [offlineConfirmed, setOfflineConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [modalError, setModalError] = useState('');
  // Effacement definitif : texte de confirmation tape + banques de la company
  // (pour redonner le N° libere a l'une d'elles).
  const [purgeConfirm, setPurgeConfirm] = useState('');
  const [companyBanks, setCompanyBanks] = useState([]);
  const [seqTarget, setSeqTarget] = useState('');
  const [newSeq, setNewSeq] = useState('');
  const [newUsername, setNewUsername] = useState('');

  const load = useCallback(async (q, archived) => {
    setLoading(true);
    setLoadError('');
    try {
      setBanks(await listAdminBanks({ q, archived }));
    } catch (err) {
      setLoadError(apiError(err, 'Chargement impossible.'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTransfers = useCallback(() => {
    listBankTransfers().then(setTransfers).catch(() => {});
  }, []);

  useEffect(() => {
    load('', false);
    loadTransfers();
    listCompanies().then(setCompanies).catch(() => {});
  }, [load, loadTransfers]);

  useEffect(() => {
    setTargetGroup('');
    setTargetGroups([]);
    if (!targetCompany) return undefined;
    // Ignore une reponse arrivee apres un changement de company (sinon les groups
    // de la company PRECEDENTE pouvaient s'afficher, 2026-10-02).
    let cancelled = false;
    listGroups(targetCompany)
      .then((list) => { if (!cancelled) setTargetGroups(list); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [targetCompany]);

  const refresh = () => { load(query.trim(), showArchived); loadTransfers(); };

  const openTransfer = (bank) => {
    setTargetCompany('');
    setNote('');
    setOfflineConfirmed(false);
    setModalError('');
    setModal({ type: 'transfer', bank });
  };

  const openRestore = (bank) => {
    setModalError('');
    setModal({ type: 'restore', bank });
  };

  const doTransfer = async () => {
    if (!targetCompany) { setModalError('Choisis la company de destination.'); return; }
    if (!offlineConfirmed) { setModalError('Coche la confirmation : plus de fiches hors ligne sur ce POS.'); return; }
    setBusy(true);
    setModalError('');
    try {
      const res = await transferAdminBank(modal.bank.id, {
        company: Number(targetCompany),
        group: targetGroup ? Number(targetGroup) : null,
        note,
        confirm_offline_synced: true,
      });
      setModal({ type: 'result', bank: modal.bank, result: res });
      refresh();
    } catch (err) {
      setModalError(apiError(err, 'Transfert impossible.'));
    } finally {
      setBusy(false);
    }
  };

  const openPurge = (bank) => {
    setPurgeConfirm('');
    setModalError('');
    setModal({ type: 'purge', bank });
  };

  const doPurge = async () => {
    setBusy(true);
    setModalError('');
    try {
      const res = await purgeAdminBank(modal.bank.id, purgeConfirm.trim());
      setSeqTarget('');
      setCompanyBanks([]);
      listAdminBanks({ company: res.company_id })
        .then((list) => setCompanyBanks(list.filter((x) => !x.is_archived)))
        .catch(() => {});
      setModal({ type: 'purged', bank: modal.bank, result: res });
      refresh();
    } catch (err) {
      setModalError(apiError(err, 'Effacement impossible.'));
    } finally {
      setBusy(false);
    }
  };

  const doSetSeq = async () => {
    if (!seqTarget) { setModalError('Choisis la banque qui doit recevoir ce N°.'); return; }
    setBusy(true);
    setModalError('');
    try {
      const row = await setAdminBankSeq(Number(seqTarget), modal.result.freed_seq);
      setModal({ type: 'seq-done', bank: modal.bank, result: modal.result, target: row });
      refresh();
    } catch (err) {
      setModalError(apiError(err, 'Changement de N° impossible.'));
    } finally {
      setBusy(false);
    }
  };

  // "Changer N°" sur une ligne (ex. donner a la nouvelle banque le N° d'une
  // banque effacee, si la fenetre apres l'effacement a ete fermee).
  const openSeq = (bank) => {
    setNewSeq('');
    setModalError('');
    setModal({ type: 'seq', bank });
  };

  const doChangeSeq = async () => {
    const n = Number(newSeq);
    if (!Number.isInteger(n) || n < 1) { setModalError('Tape un N° valide (1, 2, 3...).'); return; }
    setBusy(true);
    setModalError('');
    try {
      const row = await setAdminBankSeq(modal.bank.id, n);
      setModal({ type: 'seq-done', bank: modal.bank, target: row });
      refresh();
    } catch (err) {
      setModalError(apiError(err, 'Changement de N° impossible.'));
    } finally {
      setBusy(false);
    }
  };

  // "Changer username" (2026-10-11) : username POS provisoire en cas de
  // collision ; "Remettre l'automatique" revient au username derive du serial.
  const openUsername = (bank) => {
    setNewUsername(bank.username || '');
    setModalError('');
    setModal({ type: 'username', bank });
  };

  const doChangeUsername = async (reset) => {
    const value = newUsername.trim();
    if (!reset && !/^\d{4,8}$/.test(value)) { setModalError('Username : 4 à 8 chiffres uniquement.'); return; }
    setBusy(true);
    setModalError('');
    try {
      const row = reset ? await resetAdminBankUsername(modal.bank.id) : await setAdminBankUsername(modal.bank.id, value);
      setModal({ type: 'username-done', bank: modal.bank, target: row });
      refresh();
    } catch (err) {
      setModalError(apiError(err, 'Changement de username impossible.'));
    } finally {
      setBusy(false);
    }
  };

  const doRestore = async () => {
    setBusy(true);
    setModalError('');
    try {
      await restoreAdminBank(modal.bank.id);
      setModal({ type: 'restored', bank: modal.bank });
      refresh();
    } catch (err) {
      setModalError(apiError(err, 'Restauration impossible.'));
    } finally {
      setBusy(false);
    }
  };

  const b = modal?.bank;

  return (
    <SuperadminLayout>
      <h2 className="sab-title">Banques / POS</h2>

      <div className="sab-help">
        <strong>Comment ça marche</strong>
        <ol>
          <li><strong>Cherche</strong> le POS par son username, le nom de la banque ou la company.</li>
          <li>
            <strong>Transférer</strong> : déplace le POS vers une autre company. L&apos;ancienne banque reste
            dans sa company avec toutes ses fiches (archivée) ; une nouvelle banque est créée dans la nouvelle
            company avec le même username et un <strong>nouveau mot de passe</strong> à donner au vendeur.
          </li>
          <li>
            <strong>Restaurer</strong> : fait revenir une banque qu&apos;une company a supprimée (archivée).
            Coche « Voir les banques supprimées » pour les afficher.
          </li>
          <li>
            <strong>Effacer</strong> (superadmin principal seulement) : efface <strong>définitivement</strong>{' '}
            la banque, toutes ses fiches et ses dépôts, puis permet de donner son N° à une autre banque de la
            company. Une copie est gardée dans le journal d&apos;audit.
          </li>
          <li>
            <strong>Changer N°</strong> : donne à une banque (avec son propre POS / username) un N° libre de sa
            company — par exemple le N° d&apos;une banque que tu viens d&apos;effacer.
          </li>
          <li>
            <strong>Changer username</strong> : remplace provisoirement le username automatique du POS (ex. deux
            appareils avec le même username). Le mot de passe ne change pas. « Remettre l&apos;automatique »
            revient au username calculé depuis le serial.
          </li>
        </ol>
      </div>

      <form className="sab-search" onSubmit={(e) => { e.preventDefault(); load(query.trim(), showArchived); }}>
        <input
          type="text"
          placeholder="Username, nom de banque ou company"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <label className="sab-check">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => { setShowArchived(e.target.checked); load(query.trim(), e.target.checked); }}
          />
          Voir seulement les banques supprimées
        </label>
        <button type="submit" disabled={loading}>{loading ? '...' : 'Chercher'}</button>
      </form>

      {loadError && <p className="sab-error">{loadError}</p>}

      <table className="sab-table">
        <thead>
          <tr>
            <th>Username</th>
            <th>Banque</th>
            <th>Company</th>
            <th>Groupe</th>
            <th>Téléphone</th>
            <th>Statut</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {banks.map((row) => (
            <tr key={row.id} className={row.is_archived ? 'sab-row-archived' : ''}>
              <td>
                {row.username || '—'}
                {row.username && row.auto_username && row.username !== row.auto_username && (
                  <div className="sab-small">modifié (auto : {row.auto_username})</div>
                )}
              </td>
              <td>{row.seq} - {row.name}</td>
              <td>{row.company_code} - {row.company_name}</td>
              <td>{row.group_name || '—'}</td>
              <td>{row.phone || '—'}</td>
              <td>
                {row.is_archived
                  ? <span className="sab-badge sab-badge-archived" title={`${row.archived_by} — ${fmtDate(row.archived_at)}`}>Supprimée</span>
                  : <span className="sab-badge sab-badge-ok">Active</span>}
              </td>
              <td className="sab-row-actions">
                {row.has_device && row.username && (
                  <button type="button" className="sab-primary" onClick={() => openTransfer(row)}>
                    Transférer
                  </button>
                )}
                {row.is_archived && row.username && (
                  <button type="button" onClick={() => openRestore(row)}>Restaurer</button>
                )}
                {!row.username && <span className="sab-small">POS déjà transféré</span>}
                {!row.is_archived && (
                  <button type="button" onClick={() => openSeq(row)}>Changer N°</button>
                )}
                {!row.is_archived && row.username && (
                  <button type="button" onClick={() => openUsername(row)}>Changer username</button>
                )}
                <button type="button" className="sab-danger" onClick={() => openPurge(row)}>Effacer</button>
              </td>
            </tr>
          ))}
          {!loading && banks.length === 0 && (
            <tr><td colSpan={7} className="sab-empty">Aucune banque trouvée.</td></tr>
          )}
        </tbody>
      </table>

      <h3 className="sab-subtitle">Historique des transferts entre companies</h3>
      <table className="sab-table">
        <thead>
          <tr><th>Date</th><th>POS</th><th>De</th><th>Vers</th><th>Par</th><th>Note</th></tr>
        </thead>
        <tbody>
          {transfers.map((t) => (
            <tr key={t.id}>
              <td>{fmtDate(t.created_at)}</td>
              <td>{t.username}</td>
              <td>{t.from_company}</td>
              <td>{t.to_company}</td>
              <td>{t.moved_by}</td>
              <td>{t.note || '—'}</td>
            </tr>
          ))}
          {transfers.length === 0 && <tr><td colSpan={6} className="sab-empty">Aucun transfert pour le moment.</td></tr>}
        </tbody>
      </table>

      {modal?.type === 'transfer' && (
        <Modal title={`Transférer le POS ${b.username}`} onClose={() => !busy && setModal(null)}>
          <p className="sab-small">
            Actuellement : banque <strong>{b.seq} - {b.name}</strong> dans <strong>{b.company_name}</strong>.
          </p>
          <label className="sab-field">
            1. Vers quelle company ?
            <select value={targetCompany} onChange={(e) => setTargetCompany(e.target.value)}>
              <option value="">— choisir —</option>
              {companies.filter((c) => c.id !== b.company_id).map((c) => (
                <option key={c.id} value={c.id}>{c.code} - {c.name}</option>
              ))}
            </select>
          </label>
          <label className="sab-field">
            2. Dans quel groupe ? (optionnel)
            <select value={targetGroup} onChange={(e) => setTargetGroup(e.target.value)} disabled={!targetCompany}>
              <option value="">Groupe par défaut de la company</option>
              {targetGroups.map((g) => (
                <option key={g.id} value={g.id}>{g.display_name || g.name}</option>
              ))}
            </select>
          </label>
          <label className="sab-field">
            Note (optionnel)
            <input type="text" value={note} onChange={(e) => setNote(e.target.value)} maxLength={255} />
          </label>
          <label className="sab-check sab-warn">
            <input type="checkbox" checked={offlineConfirmed} onChange={(e) => setOfflineConfirmed(e.target.checked)} />
            3. Je confirme que ce POS n&apos;a plus de fiches vendues hors ligne en attente (sinon elles
            partiraient dans la nouvelle company).
          </label>
          {modalError && <p className="sab-error">{modalError}</p>}
          <div className="sab-actions">
            <button type="button" className="sab-primary" disabled={busy} onClick={doTransfer}>
              {busy ? 'Transfert en cours...' : 'Transférer maintenant'}
            </button>
            <button type="button" disabled={busy} onClick={() => setModal(null)}>Annuler</button>
          </div>
        </Modal>
      )}

      {modal?.type === 'result' && (
        <Modal title="POS transféré ✅" onClose={() => setModal(null)}>
          <p>
            <strong>{b.username}</strong> : {b.company_name} → <strong>{modal.result.bank.company_name}</strong>
            {' '}(banque n° {modal.result.bank.seq}, groupe {modal.result.bank.group_name || '—'}).
          </p>
          <p>Nouveau mot de passe du vendeur :</p>
          <p className="sab-password">{modal.result.password}</p>
          <p className="sab-small">
            Donne-le au vendeur : sur le POS il se reconnecte avec le même username ({b.username}) et ce mot de
            passe. Il n&apos;est affiché qu&apos;ici, note-le maintenant.
          </p>
          <div className="sab-actions">
            <button type="button" className="sab-primary" onClick={() => setModal(null)}>J&apos;ai noté, fermer</button>
          </div>
        </Modal>
      )}

      {modal?.type === 'restore' && (
        <Modal title={`Restaurer la banque ${b.username}`} onClose={() => !busy && setModal(null)}>
          <p>
            La banque <strong>{b.seq} - {b.name}</strong> redeviendra visible dans <strong>{b.company_name}</strong>,
            et le POS pourra de nouveau se connecter et vendre.
          </p>
          {modalError && <p className="sab-error">{modalError}</p>}
          <div className="sab-actions">
            <button type="button" className="sab-primary" disabled={busy} onClick={doRestore}>
              {busy ? 'Restauration...' : 'Oui, restaurer'}
            </button>
            <button type="button" disabled={busy} onClick={() => setModal(null)}>Annuler</button>
          </div>
        </Modal>
      )}

      {modal?.type === 'purge' && (
        <Modal title={`Effacer définitivement la banque ${b.seq} - ${b.name}`} onClose={() => !busy && setModal(null)}>
          <p className="sab-error">
            ⚠ La banque <strong>{b.seq} - {b.name}</strong> ({b.company_name}), <strong>toutes ses fiches</strong>{' '}
            (ventes, gains, fiches payées) et <strong>ses dépôts</strong> seront effacés. Les rapports passés de
            la company changeront. Une fiche gagnante de cette banque ne pourra plus être vérifiée ni payée.
          </p>
          <p className="sab-small">
            Une copie complète est gardée dans le journal d&apos;audit. Vérifie d&apos;abord que ce POS n&apos;a
            plus de fiches hors ligne en attente.
          </p>
          <label className="sab-field">
            Pour confirmer, tape : <strong>EFFACER {b.seq}</strong>
            <input type="text" value={purgeConfirm} onChange={(e) => setPurgeConfirm(e.target.value)} />
          </label>
          {modalError && <p className="sab-error">{modalError}</p>}
          <div className="sab-actions">
            <button
              type="button"
              className="sab-danger"
              disabled={busy || purgeConfirm.trim().toUpperCase() !== `EFFACER ${b.seq}`}
              onClick={doPurge}
            >
              {busy ? 'Effacement...' : 'Effacer définitivement'}
            </button>
            <button type="button" disabled={busy} onClick={() => setModal(null)}>Annuler</button>
          </div>
        </Modal>
      )}

      {modal?.type === 'purged' && (
        <Modal title="Banque effacée" onClose={() => !busy && setModal(null)}>
          <p>
            <strong>{b.seq} - {b.name}</strong> effacée : {modal.result.tickets} fiche(s),{' '}
            {modal.result.lines} ligne(s), {modal.result.deposits} dépôt(s).
          </p>
          <p>
            Le <strong>N° {modal.result.freed_seq}</strong> est libre. Donne-le à une autre banque de{' '}
            {b.company_name} — par exemple la nouvelle banque que tu as créée, avec son propre POS et son
            propre username :
          </p>
          <label className="sab-field">
            Banque qui reçoit le N° {modal.result.freed_seq}
            <select value={seqTarget} onChange={(e) => setSeqTarget(e.target.value)}>
              <option value="">— choisir —</option>
              {companyBanks.map((x) => (
                <option key={x.id} value={x.id}>{x.seq} - {x.name}{x.username ? ` (${x.username})` : ''}</option>
              ))}
            </select>
          </label>
          <p className="sab-small">
            Si cette banque a le mot de passe standard (calculé avec son N°), il sera recalculé avec le
            N° {modal.result.freed_seq} et affiché à l&apos;étape suivante. Un mot de passe personnalisé ne change pas.
            Le vendeur doit ensuite se (re)connecter pour que ses fiches impriment le bon REF.
          </p>
          {modalError && <p className="sab-error">{modalError}</p>}
          <div className="sab-actions">
            <button type="button" className="sab-primary" disabled={busy} onClick={doSetSeq}>
              {busy ? '...' : `Donner le N° ${modal.result.freed_seq}`}
            </button>
            <button type="button" disabled={busy} onClick={() => setModal(null)}>Plus tard</button>
          </div>
        </Modal>
      )}

      {modal?.type === 'seq' && (
        <Modal title={`Changer le N° de la banque ${b.seq} - ${b.name}`} onClose={() => !busy && setModal(null)}>
          <p className="sab-small">
            Banque <strong>{b.seq} - {b.name}</strong>{b.username ? ` (POS ${b.username})` : ''} dans{' '}
            <strong>{b.company_name}</strong>. Le N° choisi doit être libre dans cette company (par exemple celui
            d&apos;une banque effacée).
          </p>
          <label className="sab-field">
            Nouveau N°
            <input
              type="number"
              min="1"
              value={newSeq}
              onChange={(e) => setNewSeq(e.target.value)}
            />
          </label>
          <p className="sab-small">
            Si la banque a le mot de passe standard, il sera recalculé avec ce N° et affiché à l&apos;étape suivante.
          </p>
          {modalError && <p className="sab-error">{modalError}</p>}
          <div className="sab-actions">
            <button type="button" className="sab-primary" disabled={busy} onClick={doChangeSeq}>
              {busy ? '...' : 'Changer le N°'}
            </button>
            <button type="button" disabled={busy} onClick={() => setModal(null)}>Annuler</button>
          </div>
        </Modal>
      )}

      {modal?.type === 'username' && (
        <Modal title={`Changer le username du POS ${b.username}`} onClose={() => !busy && setModal(null)}>
          <p className="sab-small">
            Banque <strong>{b.seq} - {b.name}</strong> dans <strong>{b.company_name}</strong>.
            {b.auto_username && <> Username automatique : <strong>{b.auto_username}</strong>.</>}
          </p>
          <label className="sab-field">
            Nouveau username (4 à 8 chiffres)
            <input
              type="text"
              inputMode="numeric"
              maxLength={8}
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value.replace(/\D/g, ''))}
            />
          </label>
          <p className="sab-small">
            Le mot de passe du vendeur ne change pas. Il se reconnecte sur le POS en tapant ce nouveau username ;
            le POS le retient ensuite (jusqu&apos;à un reset de l&apos;appareil, qui repropose l&apos;automatique).
          </p>
          {modalError && <p className="sab-error">{modalError}</p>}
          <div className="sab-actions">
            <button type="button" className="sab-primary" disabled={busy} onClick={() => doChangeUsername(false)}>
              {busy ? '...' : 'Changer le username'}
            </button>
            {b.auto_username && b.username !== b.auto_username && (
              <button type="button" disabled={busy} onClick={() => doChangeUsername(true)}>
                Remettre l&apos;automatique ({b.auto_username})
              </button>
            )}
            <button type="button" disabled={busy} onClick={() => setModal(null)}>Annuler</button>
          </div>
        </Modal>
      )}

      {modal?.type === 'username-done' && (
        <Modal title="Username changé ✅" onClose={() => setModal(null)}>
          <p>
            La banque <strong>{modal.target.seq} - {modal.target.name}</strong> se connecte maintenant avec le
            username <strong>{modal.target.username}</strong> (mot de passe inchangé).
          </p>
          <p className="sab-small">
            Le vendeur doit se déconnecter du POS puis se reconnecter en tapant ce username.
          </p>
          <div className="sab-actions">
            <button type="button" className="sab-primary" onClick={() => setModal(null)}>Fermer</button>
          </div>
        </Modal>
      )}

      {modal?.type === 'seq-done' && (
        <Modal title="N° attribué ✅" onClose={() => setModal(null)}>
          <p>
            La banque <strong>{modal.target.name}</strong>
            {modal.target.username ? ` (POS ${modal.target.username})` : ''} porte maintenant le{' '}
            <strong>N° {modal.target.seq}</strong>.
          </p>
          {modal.target.password_changed ? (
            <>
              <p>Nouveau mot de passe du vendeur (username {modal.target.username}) :</p>
              <p className="sab-password">{modal.target.password}</p>
              <p className="sab-small">
                Il n&apos;est affiché qu&apos;ici, note-le. Le vendeur se reconnecte avec son username et ce mot de passe.
              </p>
            </>
          ) : (
            <p className="sab-small">
              {modal.target.username
                ? 'Mot de passe inchangé (mot de passe personnalisé). Le vendeur doit se déconnecter puis se reconnecter.'
                : "Cette banque n'a pas encore de POS : son mot de passe sera calculé avec ce N° quand l'appareil lui sera attribué."}
            </p>
          )}
          <div className="sab-actions">
            <button type="button" className="sab-primary" onClick={() => setModal(null)}>Fermer</button>
          </div>
        </Modal>
      )}

      {modal?.type === 'restored' && (
        <Modal title="Banque restaurée ✅" onClose={() => setModal(null)}>
          <p><strong>{b.username}</strong> est de nouveau active dans <strong>{b.company_name}</strong>.</p>
          <div className="sab-actions">
            <button type="button" className="sab-primary" onClick={() => setModal(null)}>Fermer</button>
          </div>
        </Modal>
      )}
    </SuperadminLayout>
  );
};

export default SuperadminBanksPage;
